import 'Styles/Common.scss';
import 'Styles/Carousel.scss';
import './index.scss';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { UserDataProvider } from './Foundation/User/UserContext';
import { LoadAnalyticsHTML } from 'Utilities/Analytics/Analytics';
import { AlertsContextProvider } from 'Feature/Alerts/AlertsContext';
import { SliderContextProvider } from 'Feature/Slider/SliderContext';
import { GlobalSearchProvider } from 'Feature/Coveo/GlobalSearchContext';
import { isOneBrand, isPL, isProd } from './Foundation/Configurations/GlobalConfiguration';
import { QuoteContextProvider } from 'Feature/Quoting/QuoteContext';
import { AppRoutes } from './AppRoutes';
import { QuoteDataContextProvider } from 'Foundation/User/QuoteDataContext';
import { CoveoAnalytics } from 'Feature/Coveo/CoveoAnalytics';
import { AgencyMetricsContextProvider } from 'Foundation/Agency/AgencyMetricsContext';
import { NewsArticleContextProvider } from 'Feature/PageContent/News/NewsArticleContext';
import { CoveoRecommendationEngineProvider } from 'Feature/Coveo/CoveoRecommendationEngine';
import HeapWaypointManager from 'Utilities/Analytics/HeapWaypointManager';
import { getAssetUrl } from 'Utilities/assetBase';

const faviconPL = getAssetUrl('images/favicon_pl.png');
const faviconBL = getAssetUrl('images/favicon_bl.ico');

export const App = () => {
  useEffect(() => {
    setFavicon();
    loadTealium();
    setCSSDomain();
  }, []);

  const setFavicon = () => {
    try {
      // Try and setup a dynamic favicon based on domain
      let link: HTMLLinkElement | null =
        document.querySelector('link[rel="shortcut icon"]') || document.querySelector('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.id = 'favicon';
        link.rel = 'shortcut icon';
        document.head.appendChild(link);
      }
      link.href = isPL() && !isOneBrand() ? faviconPL : faviconBL;
    } catch (err) {
      console.error(err);
    }
  };

  const loadTealium = () => {
    try {
      // Load Tealium CPRA based on environment
      const script: HTMLScriptElement = document.createElement('script');
      script.src = isProd()
        ? 'https://tags.tiqcdn.com/utag/libertymutual/lmgp/prod/utag.js'
        : 'https://tags.tiqcdn.com/utag/libertymutual/lmgp/dev/utag.js';
      script.async = true;
      document.head.appendChild(script);
    } catch (err) {
      console.error(err);
    }
  };

  const setCSSDomain = () => {
    try {
      document.body.classList.add(isPL() ? 'pl' : 'bl');
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <ErrorBoundary fallback={<div style={{ display: 'none' }}>{`ERROR in: LoadAnalyticsHTML`}</div>}>
        <LoadAnalyticsHTML />
      </ErrorBoundary>
      <ErrorBoundary fallback={<div style={{ display: 'none' }}>{`ERROR in: One of the Context Providers`}</div>}>
        <UserDataProvider>
          <GlobalSearchProvider>
            <CoveoRecommendationEngineProvider analyticsEnabled={true}>
              <SliderContextProvider>
                <AlertsContextProvider>
                  <QuoteContextProvider>
                    <QuoteDataContextProvider>
                      <NewsArticleContextProvider>
                        <AgencyMetricsContextProvider>
                          <ErrorBoundary fallback={<div style={{ display: 'none' }}>{`ERROR in: AppRoutes`}</div>}>
                            <AppRoutes />
                            <CoveoAnalytics />
                            {/*Keep heap waypoint manager as the last component*/}
                            <HeapWaypointManager />
                          </ErrorBoundary>
                        </AgencyMetricsContextProvider>
                      </NewsArticleContextProvider>
                    </QuoteDataContextProvider>
                  </QuoteContextProvider>
                </AlertsContextProvider>
              </SliderContextProvider>
            </CoveoRecommendationEngineProvider>
          </GlobalSearchProvider>
        </UserDataProvider>
      </ErrorBoundary>
    </>
  );
};
